import React from "react";
import { Slider as CompoundSlider, Handles, Tracks, Rail, Ticks } from "react-compound-slider";
import MtiSliderRail from "../Controls/MtiSliderRail";
import { MtiSliderHandle } from "../Controls/MtiSliderHandle";
import { MtiSliderTrack } from "../Controls/MtiSliderTrack";

type TickProps = { count: number; min: number; max: number; index: number; style?: React.CSSProperties };

function Tick({ count, min, max, index, style = {} }: TickProps) {
  const percent = index / (count - 1);
  let value = (max - min) * percent + min;
  value = parseFloat(value.toFixed(1));
  return (
    <div
      style={{
        position: "absolute",
        marginTop: 4,
        fontSize: 10,
        textAlign: index === 0? "left" : index === (count - 1)? "right" : "center",
        marginLeft: `${(-(100 / count) / (count - 1)) * index}%`,
        width: `${100 / count}%`,
        left: `${percent * 100}%`,
        lineHeight: "14px",
        color: "#4F4F4F",
        WebkitTouchCallout: "none", 
        WebkitUserSelect: "none", 
        KhtmlUserSelect: "none", 
        MozUserSelect: "none", 
        userSelect: "none",
        ...style,
      }}
    >
      {value}
    </div>
  );
}

type Props = {
  disabled: boolean;
  value: number;
  min: number;
  max: number;
  step: number;
  onHandleDraged: (value: number) => void;
  onValueChanged: (value: number) => void;
  style?: React.CSSProperties;
};

function ImagerFeatureControlSlider({
  disabled,
  value,
  min,
  max,
  step,
  onHandleDraged,
  onValueChanged,
  style = {},
}: Props) {
  const domain: [number, number] = [min, max];

  const onChangeSlider = (newValues: readonly number[]) => {
    if (disabled) {
      return;
    }

    if (onValueChanged === null || onValueChanged === undefined) {
      return;
    }

    console.log("SLIDER_CHANGE", { newValues }, { value });

    if (Math.abs(value - newValues[0]) > step) {
      onValueChanged(newValues[0]);
    }
  };

  const onUpdateSlider = (newValues: readonly number[]) => {
    if (disabled) {
      return;
    }
    if (onHandleDraged === null || onHandleDraged === undefined) {
      return;
    }

    console.log("SLIDER_UPDATE", { newValues }, { value });

    onHandleDraged(newValues[0]);
  };

  const roundedValue = Math.round(value / step) * step;

  console.log("SLIDER_CREATED", { value }, { roundedValue });

  return (
    <div
      style={{
        minHeight: 35,
        ...style,
      }}
    >
      <CompoundSlider
        disabled={disabled}
        rootStyle={{
          position: "relative",
          top: 17,
          left: 12,
          width: "calc(100% - 24px)",
          height: 24,
        }}
        domain={domain}
        step={step}
        mode={1}
        values={[roundedValue]}
        onUpdate={onUpdateSlider}
        onChange={onChangeSlider}
      >
        <Rail>{({ getRailProps }) => <MtiSliderRail getRailProps={getRailProps} />}</Rail>
        <Handles>
          {({ handles, getHandleProps }) => (
            <div className="slider-handles">
              {handles.map((handle) => (
                <MtiSliderHandle
                  key={handle.id}
                  handle={handle}
                  domain={domain}
                  getHandleProps={getHandleProps}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
        </Handles>
        <Tracks right={false}>
          {({ tracks, getTrackProps }) => (
            <div className="slider-tracks">
              {tracks.map(({ id, source, target }) => (
                <MtiSliderTrack
                  key={id}
                  source={source}
                  target={target}
                  getTrackProps={getTrackProps}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
        </Tracks>
        <Ticks count={3}>
          {({ ticks }) => (
            <div className="slider-ticks">
              <Tick key={"tick" + 0} index={0} count={3} min={min} max={max}/>
              <Tick key={"tick" + 1} index={1} count={3} min={min} max={max}/>
              <Tick key={"tick" + 2} index={2} count={3} min={min} max={max}/>
            </div>
          )}
        </Ticks>
      </CompoundSlider>
    </div>
  );
}

function arePropsEqual(prevProps: Props, nextProps: Props) {
  return prevProps.value === nextProps.value && prevProps.disabled === nextProps.disabled;
}
export default React.memo(ImagerFeatureControlSlider, arePropsEqual);
